import type { GoogleMapStyle } from './googleMapTypes'

export const GOOGLE_MAP_LIGHT_MAP_ID = '49ae42fed52588c3'

export const GOOGLE_MAP_TYPE_IDS = {
  street: 'roadmap' as google.maps.MapTypeId,
  satellite: 'hybrid' as google.maps.MapTypeId,
} as const satisfies Record<GoogleMapStyle, google.maps.MapTypeId>

export const GOOGLE_MAP_DEFAULT_OPTIONS = {
  clickableIcons: false,
  disableDefaultUI: true,
  gestureHandling: 'greedy',
  isFractionalZoomEnabled: true,
  renderingType: 'VECTOR',
} as const

export function getGoogleMapsApiKey() {
  if (typeof window === 'undefined') return ''
  return window.ENV?.GOOGLE_MAPS_API_KEY ?? ''
}

export const GOOGLE_MAPS_API_LIBRARIES = ['places', 'geocoding']

export const GOOGLE_MAP_DEBUG_RASTER_PARAM = 'debugRasterMap'

export type GoogleRasterRenderingBrowserCheck = {
  error?: string
  hasWebGl: boolean
  isSoftwareRenderer: boolean
  renderer: string
  shouldUseRaster: boolean
}

const SOFTWARE_WEBGL_RENDERERS = [
  'llvmpipe',
  'softpipe',
  'software',
  'swiftshader',
]

let cachedRasterRenderingBrowserCheck:
  | GoogleRasterRenderingBrowserCheck
  | undefined
let forcedGoogleRasterRendering = false

export function getGoogleMapTypeId(mapStyle: GoogleMapStyle) {
  return GOOGLE_MAP_TYPE_IDS[mapStyle]
}

export function isGoogleMapDebugRasterSearch(search: string) {
  return new URLSearchParams(search).get(GOOGLE_MAP_DEBUG_RASTER_PARAM) === '1'
}

export function shouldUseGoogleRasterRenderingForBrowser() {
  return getGoogleRasterRenderingBrowserCheck().shouldUseRaster
}

export function forceGoogleRasterRendering() {
  forcedGoogleRasterRendering = true
}

export function isGoogleRasterRenderingForced() {
  return forcedGoogleRasterRendering
}

export function getGoogleRasterRenderingBrowserCheck(): GoogleRasterRenderingBrowserCheck {
  if (cachedRasterRenderingBrowserCheck) {
    return cachedRasterRenderingBrowserCheck
  }

  if (typeof document === 'undefined') {
    cachedRasterRenderingBrowserCheck = {
      hasWebGl: false,
      isSoftwareRenderer: false,
      renderer: '',
      shouldUseRaster: false,
    }
    return cachedRasterRenderingBrowserCheck
  }

  try {
    const canvas = document.createElement('canvas')
    const context =
      canvas.getContext('webgl2') ||
      canvas.getContext('webgl') ||
      canvas.getContext('experimental-webgl')

    if (!context) {
      cachedRasterRenderingBrowserCheck = {
        hasWebGl: false,
        isSoftwareRenderer: false,
        renderer: '',
        shouldUseRaster: true,
      }
      return cachedRasterRenderingBrowserCheck
    }

    const renderer = getWebGlRenderer(context)
    const isSoftwareRenderer = isSoftwareWebGlRenderer(renderer)

    if (isWebGlContext(context)) {
      context.getExtension('WEBGL_lose_context')?.loseContext()
    }

    canvas.remove()

    cachedRasterRenderingBrowserCheck = {
      hasWebGl: true,
      isSoftwareRenderer,
      renderer,
      shouldUseRaster: isSoftwareRenderer,
    }
    return cachedRasterRenderingBrowserCheck
  } catch (error) {
    cachedRasterRenderingBrowserCheck = {
      error: error instanceof Error ? error.message : String(error),
      hasWebGl: false,
      isSoftwareRenderer: false,
      renderer: '',
      shouldUseRaster: true,
    }
    return cachedRasterRenderingBrowserCheck
  }
}

function getWebGlRenderer(
  context: WebGLRenderingContext | WebGL2RenderingContext | RenderingContext
) {
  if (!isWebGlContext(context)) return ''

  const debugRendererInfo = context.getExtension('WEBGL_debug_renderer_info')
  const renderer = debugRendererInfo
    ? context.getParameter(debugRendererInfo.UNMASKED_RENDERER_WEBGL)
    : context.getParameter(context.RENDERER)

  return typeof renderer === 'string' ? renderer : ''
}

function isWebGlContext(
  context: RenderingContext
): context is WebGLRenderingContext | WebGL2RenderingContext {
  return 'getExtension' in context && 'getParameter' in context
}

function isSoftwareWebGlRenderer(renderer: string) {
  const normalizedRenderer = renderer.toLowerCase()

  return SOFTWARE_WEBGL_RENDERERS.some(softwareRenderer =>
    normalizedRenderer.includes(softwareRenderer)
  )
}
